/**
 * Final rule requirements and where they land, shared by every screen that
 * shows them (operator order 2026-09-15):
 *
 *   TakeOnBlock   on ONE policy: the requirements compliance assigned to this
 *                 policy, or the reader placed here and was sure about.
 *   RuleHomesList for compliance, a department owner and RuleIQ: every
 *                 requirement no policy covers, with the recommended home or
 *                 the words "not sure which policy should take this", and,
 *                 for compliance, the decision controls.
 *
 * Plain words only. The regulation's own sentence is always shown.
 */
import React, { useEffect, useState } from 'react';

const API = process.env.REACT_APP_API_URL;

const CHANGE_LABEL = { new: 'New', changed: 'Changed', removed: 'Removed' };
export function changeLabel(r) {
  if (r.kind === 'removed') return 'Removed from the regulation';
  if (r.kind === 'relaxation') return 'Requirement eased';
  return `${CHANGE_LABEL[r.change] || 'Changed'} requirement`;
}

function Quote({ text, max = 420 }) {
  const t = String(text || '');
  if (!t) return null;
  return <blockquote className="text-[12.5px] italic text-slate-600 mt-1 leading-relaxed">&ldquo;{t.slice(0, max)}{t.length > max ? '…' : ''}&rdquo;</blockquote>;
}

/** One requirement, as a policy owner reads it. */
export function RequirementCard({ r, children, compact = false }) {
  return (
    <div className={`mt-2.5 border-l-2 pl-3 ${r.kind === 'removed' ? 'border-rose-300' : 'border-orange-300'}`}>
      <p className={`${compact ? 'text-xs' : 'text-[12.5px]'} font-medium text-slate-800`}>{r.title}</p>
      <p className="text-[12px] font-semibold text-slate-600 mt-0.5">{changeLabel(r)} · {r.rule_title || 'Final rule'} · 42 CFR {r.cfr_section}</p>
      {r.plain && <p className={`${compact ? 'text-xs' : 'text-[12.5px]'} text-slate-700 mt-0.5 leading-relaxed`}>{r.plain}</p>}
      <Quote text={r.quote} />
      {children}
    </div>
  );
}

/**
 * On a policy: what this policy should take on. `items` is guidance.take_on.
 */
export function TakeOnBlock({ items, compact = false }) {
  if (!Array.isArray(items) || !items.length) return null;
  return (
    <div className={`rounded-lg bg-white ring-1 ring-orange-200 ${compact ? 'p-3' : 'p-3.5'}`}>
      <p className={`${compact ? 'text-xs' : 'text-[13px]'} font-semibold text-slate-800`}>Add to this policy: requirements from the final rule no policy covers yet</p>
      <p className="text-[12px] text-slate-500 mt-0.5">Each one is new or changed in the rule, no policy in the organization states it, and this policy was named as the place for it. Write it in, or tell compliance it belongs elsewhere.</p>
      {items.map((r) => (
        <RequirementCard key={r.id} r={r} compact={compact}>
          <p className="text-[11.5px] text-slate-500 mt-1">
            {r.decided
              ? `Assigned to this policy by ${r.decided.by || 'compliance'}${r.decided.note ? `: ${r.decided.note}` : ''}.`
              : `Placed here by the reader${r.recommended_reason ? `: ${r.recommended_reason}` : ''}. Compliance can move it.`}
          </p>
        </RequirementCard>
      ))}
    </div>
  );
}

function homeLine(h) {
  if (h.decided) {
    if (h.decided.new_policy) return { text: `Compliance decided a new policy is needed${h.decided.by ? ` (${h.decided.by})` : ''}.`, tone: 'text-slate-700' };
    return { text: `Compliance assigned it to ${h.decided.policy_number ? `${h.decided.policy_number} ` : ''}${h.decided.title || 'a policy'}${h.decided.by ? ` (${h.decided.by})` : ''}.`, tone: 'text-slate-700' };
  }
  if (h.status === 'unsure') {
    return { text: `Not sure which policy should take this.${h.recommended ? ` The reader leaned to ${h.recommended.policy_number || ''} ${h.recommended.title || ''} but could not confirm it.` : ''} Compliance decides.`, tone: 'text-amber-800' };
  }
  if (h.needs_new_policy) return { text: `No existing policy is a fit; a new policy is needed.${h.recommended_reason ? ` ${h.recommended_reason}` : ''}`, tone: 'text-slate-700' };
  if (h.recommended) return { text: `Should go in ${h.recommended.policy_number || ''} ${h.recommended.title || ''}.${h.recommended_reason ? ` ${h.recommended_reason}` : ''}`, tone: 'text-slate-700' };
  return { text: 'No policy covers this yet.', tone: 'text-slate-700' };
}

/**
 * The list of requirements no policy covers. `canDecide` shows the controls
 * (compliance); `policies` is the org's policy list for the picker;
 * `decideUrl(h)` builds the POST endpoint.
 */
export function RuleHomesList({ homes, canDecide = false, policies = [], decideUrl = null, onChanged = null, compact = false, emptyText = 'Every requirement of the final rule is covered by a policy.' }) {
  const open = (homes || []).filter((h) => h.status !== 'covered' || h.decided);
  const [busy, setBusy] = useState(null);
  const [pick, setPick] = useState({});
  if (!open.length) return <p className="text-[12.5px] text-slate-500">{emptyText}</p>;
  const decide = async (h, body) => {
    if (!decideUrl) return;
    setBusy(h.id);
    try {
      const d = await fetch(decideUrl(h), { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }).then((r) => r.json());
      if (!d.success) window.alert(d.message || 'That did not go through.');
      onChanged?.();
    } catch { window.alert('That did not go through.'); }
    setBusy(null);
  };
  return (
    <div className="space-y-1">
      {open.map((h) => {
        const line = homeLine(h);
        return (
          <RequirementCard key={h.id} r={h} compact={compact}>
            <p className={`text-[12px] mt-1 ${line.tone}`}>{line.text}</p>
            {h.department_name && <p className="text-[11.5px] text-slate-500 mt-0.5">Department: {h.department_name}</p>}
            {canDecide && (
              <div className="flex flex-wrap items-center gap-2 mt-1.5">
                <select value={pick[h.id] || ''} onChange={(e) => setPick((p) => ({ ...p, [h.id]: e.target.value }))} className="text-[12px] border border-slate-300 rounded-md px-2 py-1 bg-white max-w-[320px]">
                  <option value="">Choose the policy that takes it…</option>
                  {policies.map((p) => <option key={p.id} value={p.id}>{p.policy_number} · {p.title}</option>)}
                </select>
                <button type="button" disabled={busy === h.id || !pick[h.id]} onClick={() => decide(h, { policy_id: pick[h.id] })} className="text-[12px] px-2.5 py-1 rounded-md bg-slate-900 text-white disabled:opacity-40">Assign</button>
                <button type="button" disabled={busy === h.id} onClick={() => decide(h, { new_policy: true })} className="text-[12px] px-2.5 py-1 rounded-md border border-slate-300 text-slate-700">New policy needed</button>
                {h.decided && <button type="button" disabled={busy === h.id} onClick={() => decide(h, { clear: true })} className="text-[12px] text-slate-500 underline">Undo</button>}
              </div>
            )}
          </RequirementCard>
        );
      })}
    </div>
  );
}

/** Loads the org's policies for the picker once. */
export function usePolicyChoices(enabled) {
  const [policies, setPolicies] = useState([]);
  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    fetch(`${API}/api/ppm/policies?limit=500`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (alive && d?.success) setPolicies((d.policies || []).map((p) => ({ id: p.id, policy_number: p.policy_number, title: p.title }))); })
      .catch(() => {});
    return () => { alive = false; };
  }, [enabled]);
  return policies;
}
